import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import ReviewText from "@/components/site/ReviewText";

const FALLBACK = [
  { id: "r1", author: "Familie Becker", rating: 5, text: "Absolut sauber gearbeitet, pünktlich und freundlich. Unsere Wohnung sieht aus wie neu – wir empfehlen den Betrieb gerne weiter!", date: "März 2025" },
  { id: "r2", author: "Markus L.", rating: 5, text: "Fachlich top, ein echter Meister seines Fachs. Die Beratung war ehrlich und das Ergebnis übertrifft unsere Erwartungen.", date: "Februar 2025" },
  { id: "r3", author: "Sabine H.", rating: 5, text: "Vom ersten Kontakt bis zur Endabnahme: höflich, professionell, präzise. Auch der Preis war absolut fair.", date: "Januar 2025" },
];

const INITIAL_LIMIT = 6;

export default function Reviews({ items, siteInfo }) {
  const [showAll, setShowAll] = useState(false);
  const display = items && items.length > 0 ? items : FALLBACK;
  const visible = showAll ? display : display.slice(0, INITIAL_LIMIT);
  const computedAvg = display.length
    ? (display.reduce((a, r) => a + (r.rating || 5), 0) / display.length)
    : 5;
  const ratingText = siteInfo?.google_rating || computedAvg.toFixed(1);
  const totalCount = siteInfo?.google_reviews_count > 0 ? siteInfo.google_reviews_count : display.length;
  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32 bg-sand-200">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 mb-16 gap-8">
          <div className="md:col-span-6">
            <p className="text-xs uppercase tracking-[0.25em] text-sage font-bold mb-6" data-testid="reviews-overline">Google Bewertungen</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-stone-900 leading-tight" data-testid="reviews-headline">
              Was unsere Kunden <em className="text-clay">sagen</em>.
            </h2>
          </div>
          <div className="md:col-span-6 md:flex md:items-end md:justify-end">
            <div className="flex items-center gap-3" data-testid="reviews-rating-summary">
              <div className="flex">
                {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-clay text-clay" />)}
              </div>
              <p className="font-display text-2xl text-stone-900">{ratingText} / 5</p>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-600">aus {totalCount}+ Bewertungen</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {visible.map((r, i) => (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`review-card-${i}`}
              className="p-8 bg-white border border-sand-300 rounded-sm relative flex flex-col"
            >
              <Quote className="w-8 h-8 text-clay/30 absolute top-6 right-6" strokeWidth={1.5} />
              <div className="flex mb-5">
                {Array.from({ length: r.rating || 5 }).map((_, idx) => (
                  <Star key={idx} className="w-4 h-4 fill-clay text-clay" />
                ))}
              </div>
              <p className="text-stone-700 leading-relaxed font-display text-xl italic mb-6 flex-1" data-testid={`review-text-${i}`}>
                <ReviewText
                  text={r.text}
                  threshold={260}
                  buttonClassName="not-italic font-body text-sm text-clay hover:text-clay-hover font-medium tracking-normal"
                />
              </p>
              <div className="pt-5 border-t border-sand-300 mt-auto">
                <p className="text-stone-900 font-medium" data-testid={`review-author-${i}`}>{r.author}</p>
                {r.date && <p className="text-xs uppercase tracking-[0.2em] text-stone-600 mt-1">{r.date}</p>}
              </div>
            </motion.article>
          ))}
        </div>

        {display.length > INITIAL_LIMIT && (
          <div className="text-center mt-12">
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              data-testid="reviews-toggle"
              className="px-7 py-3 border border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-sand-100 transition-colors duration-300 rounded-sm text-sm uppercase tracking-wider"
            >
              {showAll ? "Weniger anzeigen" : `Alle ${display.length} Bewertungen anzeigen`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
